/**
 * B000170 仕入先照会
 */

import { Column, Query } from "@material-table/core";
import { B000170Find, B000170FindRequest, catchRestError } from "clients/rest";
import {
  msgState,
  SCodeSelect,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  SText,
  STextField,
} from "components";
import { Shiiresakibun } from "messages/codes";
import { labels } from "messages/labels";
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { focusState, SDialogContext, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 削除予定区分チェック除外区分
  deljogaiKbn: string;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  shiresakiCdFrom: string;
  shiresakiCdTo: string;
  shiresakiNm: string;
  shiresakiKana: string;
  torihikisakibun: string;
  torihikisakibunCd: string;
  torihikisakibunNm: string;
  // 削除予定区分チェック除外区分(1の場合、削除予定区分を無視)
  deljogaiKbn: string;
}>;

// テーブル定義
type Data = Partial<{
  gyoNo: number;
  shireCd: string;
  shainCd: string;
  shireNm1: string;
  shireNm2: string;
  shireNmr: string;
  shireKana: string;
  postCd: string;
  address1: string;
  address2: string;
  telNo: string;
  faxNo: string;
  shokuchiKbn: string;
  torihikikaishiYmd: string;
  torihikishuryoYmd: string;
  shirebunCd1: string;
  shirebunCd2: string;
  shirebunCd3: string;
  shirebunCd4: string;
  shirebunCd5: string;
  sakujoyoteiKbn: string;
  version: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  shiresakiCdFrom: "",
  shiresakiCdTo: "",
  shiresakiNm: "",
  shiresakiKana: "",
  torihikisakibun: "",
  torihikisakibunCd: "",
  torihikisakibunNm: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "3rem", align: "right" },
  { field: "shireCd", title: "仕入先コード", width: "5rem" },
  { field: "shainCd", hidden: true },
  { field: "shireNm1", title: "仕入先名", width: "20rem" },
  { field: "shireNm2", hidden: true },
  { field: "shireNmr", hidden: true },
  { field: "shireKana", title: "仕入先カナ", width: "20rem" },
  { field: "postCd", hidden: true },
  { field: "address1", hidden: true },
  { field: "address2", hidden: true },
  { field: "telNo", hidden: true },
  { field: "faxNo", hidden: true },
  { field: "shokuchiKbn", hidden: true },
  { field: "torihikikaishiYmd", hidden: true },
  { field: "torihikishuryoYmd", hidden: true },
  { field: "shirebunCd1", hidden: true },
  { field: "shirebunCd2", hidden: true },
  { field: "shirebunCd3", hidden: true },
  { field: "shirebunCd4", hidden: true },
  { field: "shirebunCd5", hidden: true },
  { field: "sakujoyoteiKbn", hidden: true },
  { field: "version", hidden: true },
];

const focusId = "B000170";

export const B000170 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // ダイアログの開閉を行う関数
  const handleSubmit = useContext(SDialogContext);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス関数
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  //初期表示
  useEffect(() => {
    // 削除予定区分チェック除外区分処理
    if (props.deljogaiKbn) {
      methods.setValue("deljogaiKbn", props.deljogaiKbn);
    }
    focus(1);
  });

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する
      const fields = methods.getValues();

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B000170FindRequest = {
        shireCdFrom: fields.shiresakiCdFrom,
        shireCdTo: fields.shiresakiCdTo,
        name: fields.shiresakiNm,
        kana: fields.shiresakiKana,
        torihikisakibun: fields.torihikisakibun,
        shirebunCd: fields.torihikisakibunCd,
        // 削除予定区分チェック除外区分
        deljogaiKbn: fields.deljogaiKbn,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        sort: ["shireCd,asc"],
      };
      // API呼出し
      B000170Find(param)
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({ data: [], page: 0, totalCount: 0 });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="仕入先照会"
        gridTemplateColumns="6rem 15rem 3rem 13rem 2rem"
      >
        <SLabel>仕入先コード</SLabel>
        <SNumberField name="shiresakiCdFrom" code size={6} {...register(1)} />
        <SText>～</SText>
        <SNumberField name="shiresakiCdTo" code size={6} {...register(2)} />
        <SLabel gridColumn="1">仕入先名</SLabel>
        <STextField
          name="shiresakiNm"
          size={40}
          gridColumn="span 3"
          {...register(3)}
        />
        <SLabel gridColumn="1">カナ名</SLabel>
        <STextField
          name="shiresakiKana"
          size={40}
          gridColumn="span 3"
          {...register(4)}
        />
        <SLabel gridColumn="1">取引先分類</SLabel>
        <SCodeSelect
          name="torihikisakibun"
          options={Shiiresakibun}
          {...register(5)}
        />
        <SNumberField name="torihikisakibunCd" code size={3} {...register(6)} />
        <STextField name="torihikisakibunNm" size={10} disabled />
        <SNormalButton
          label={labels.buttons.search}
          gridRow="5"
          gridColumn="5"
          onClick={handleClickSearchButton}
          {...register(7)}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
        />
      </SFrame>
    </FormProvider>
  );
};
